import { differenceInMinutes } from "date-fns";
import { Loader2, RotateCcw } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import JobRunDetails from "src/components/JobRunDetails";
import { ScopeGate } from "src/components/ScopeGate";
import { Button } from "src/components/ui/button";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { useEmbeddedChat } from "src/context/EmbeddedChatContext";
import { useToast } from "src/hooks/use-toast";
import AskTierZero from "src/pages/Dagvestigations/AskTierZero";
import { AlertGroupContextBanner } from "src/pages/Dagvestigations/components/AlertGroupContextBanner";

import { DagvestigationJobDetails_jobRun$key } from "./__generated__/DagvestigationJobDetails_jobRun.graphql";
import { DagvestigationJobDetailsRerunMutation } from "./__generated__/DagvestigationJobDetailsRerunMutation.graphql";

const JOB_RUN_TIMEOUT_MINUTES = 45;

const rerunJobMutation = graphql`
  mutation DagvestigationJobDetailsRerunMutation($input: RerunJobInput!) {
    rerunJob(input: $input) {
      success
      jobRun {
        id
        status
      }
    }
  }
`;

// Define fragment for investigation-specific data requirements
export const dagvestigationJobDetailsFragment = graphql`
  fragment DagvestigationJobDetails_jobRun on GraphQLJobRun {
    id
    name
    status
    updatedAt
    outputJson
    alertEvent {
      id
      alertEventGroup {
        ...AlertGroupContextBanner_alertEventGroup
      }
    }
    ...JobRunDetails_jobRun
    investigation {
      id
      summary
    }
    job {
      id
      alert {
        id
        name
      }
    }
  }
`;

interface DagvestigationJobDetailsProps {
  jobRun: DagvestigationJobDetails_jobRun$key;
}

export function DagvestigationJobDetails({ jobRun }: DagvestigationJobDetailsProps) {
  const data = useFragment(dagvestigationJobDetailsFragment, jobRun);
  const { registerContext, unregisterContext } = useEmbeddedChat();
  const { toast } = useToast();
  const [rerunQueued, setRerunQueued] = useState(false);
  const [isQueuedDialogOpen, setIsQueuedDialogOpen] = useState(false);
  const [rerunJob, isRerunInFlight] = useMutation<DagvestigationJobDetailsRerunMutation>(rerunJobMutation);

  // Register context for embedded chat
  useEffect(() => {
    const contextString = `The user is viewing an investigation. Use the jobRunId to fetch details if needed.
Respond to the user's questions about this investigation.`

    registerContext("investigation", {
      contextString,
      data: { jobRunId: data.id, type: "investigation" },
    })

    return () => {
      unregisterContext("investigation")
    }
  }, [data.id, registerContext, unregisterContext])

  const isTimedOut =
    data.status === "IN_PROGRESS" &&
    data.updatedAt &&
    differenceInMinutes(new Date(), new Date(data.updatedAt)) >= JOB_RUN_TIMEOUT_MINUTES;
  const canRerun =
    !rerunQueued &&
    (data.status === "ERRORED" || data.status === "STOPPED" || isTimedOut);

  const handleRerun = useCallback(() => {
    rerunJob({
      variables: { input: { jobRunId: data.id } },
      onCompleted: (response) => {
        if (response.rerunJob?.success) {
          setRerunQueued(true);
          setIsQueuedDialogOpen(true);
        }
      },
      onError: (error) => {
        toast({
          title: "Failed to rerun investigation",
          description: error.message || "An unexpected error occurred.",
          variant: "error",
        });
      },
    });
  }, [rerunJob, data.id, toast]);

  const alertEventGroup = data.alertEvent?.alertEventGroup ?? null;

  return (
    <div className="space-y-6">
      {alertEventGroup && (
        <AlertGroupContextBanner
          alertEventGroup={alertEventGroup}
          currentJobRunId={data.id}
        />
      )}
      <JobRunDetails
        jobRun={data}
        context="alert"
        rightContent={
          <div className="flex flex-wrap items-center gap-2">
            {canRerun && (
              <ScopeGate scope="write:alert_responders">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRerun}
                  disabled={isRerunInFlight}
                >
                  {isRerunInFlight ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Rerun
                </Button>
              </ScopeGate>
            )}
            <ScopeGate scope="write:chat">
              <AskTierZero />
            </ScopeGate>
          </div>
        }
      />
      <UIConfirmationDialog
        isOpen={isQueuedDialogOpen}
        onClose={() => setIsQueuedDialogOpen(false)}
        onConfirm={() => setIsQueuedDialogOpen(false)}
        title="Rerun Queued"
        description="This rerun has been queued and should start within the next 5 minutes. You can close this page and come back later to see the results."
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
}
